import { BattleState, Monster, TurnCommands, TurnResult, CommandType } from '../types';
import { AIConfig, SpeciesTendency } from './types';
import { buildPairScores } from './aiSelector';
import { getTendencyBySpecies } from './tendencies';
import { getHpModifiers, getReflectorModifiers, getSelfReflectorModifiers } from './situationModifiers';
import { getAIConfigModifiers } from './aiConfigModifiers';
import { inferOpponentArchetype, getArchetypeWeightedCommandBias, ArchetypePosterior } from './opponentArchetype';

/**
 * Lv6 達人 AI のゲーム木探索における相手応手予測
 *
 * 相手の手を予測する際、以下の情報を統合する:
 * 1. 相手モンスターの種族傾向（getTendencyBySpecies）
 * 2. PR3 追加: 過去の行動から推定した「アーキタイプ」（攻撃型/守備型/etc.）
 *    - 履歴がある場合、ベイズ推定で事後確率を計算
 *    - その確率で重み付けした行動傾向を speciesTendency に乗算合成
 * 3. HP・リフレクター・スタンス・距離などの状況モディファイア
 *
 * 純粋関数。同一入力に対して常に同じ結果を返す。
 */

/**
 * 相手の上位 M 個の応手候補を取得する
 *
 * @param state 現在のバトル状態
 * @param opponentPlayerId 相手のプレイヤーID
 * @param opponentMonster 相手のモンスター（自分視点では「敵」だが、この関数では予測対象）
 * @param ownMonster 自分のモンスター（相手視点では「敵」）
 * @param turnHistory ターン履歴
 * @param opponentAIConfig 相手の AIConfig（標準は DEFAULT_AI_CONFIG）
 * @param topM 取得する応手の数
 * @returns スコア降順で並んだ応手候補（最大 topM 個）
 */
export function predictOpponentReplies(
  state: BattleState,
  opponentPlayerId: 'player1' | 'player2',
  opponentMonster: Monster,
  ownMonster: Monster,
  turnHistory: TurnResult[],
  opponentAIConfig: AIConfig,
  topM: number,
): TurnCommands[] {
  // 相手視点で buildPairScores を呼ぶ（自分と相手を入れ替えて）
  const opponentSelfId = opponentPlayerId;
  const opponentOppId: 'player1' | 'player2' = opponentPlayerId === 'player1' ? 'player2' : 'player1';

  // 種族傾向にアーキタイプ推定によるバイアスを乗算合成
  const baseTendency = getTendencyBySpecies(opponentMonster.id);
  const speciesTendency = applyArchetypeBias(baseTendency, turnHistory, opponentPlayerId, opponentAIConfig);

  // HP・リフレクター・config モディファイア
  const opponentSelfState = state[opponentSelfId];
  const opponentOppState = state[opponentOppId];

  const ownHpRatio = opponentSelfState.currentHp / opponentMonster.stats.hp;
  const oppHpRatio = opponentOppState.currentHp / ownMonster.stats.hp;
  const hpMods = getHpModifiers(ownHpRatio, oppHpRatio);
  const reflectorMods = getReflectorModifiers(
    Math.max(0, ownMonster.reflector.maxReflectCount - opponentOppState.usedReflectCount),
  );
  const selfReflectorMods = getSelfReflectorModifiers(
    Math.max(0, opponentMonster.reflector.maxReflectCount - opponentSelfState.usedReflectCount),
  );
  const configMods = getAIConfigModifiers(opponentAIConfig);

  // ペア評価（counter/opponent ability/matchup/successPattern/metaReading は省略）
  // PR2 では「相手の Lv5 的な手」を粗く取得すれば十分。
  // 厳密な Lv5 全モディファイア合成は重いため、軽量モードで扱う。
  const pairScores = buildPairScores(
    state,
    opponentSelfId,
    opponentMonster,
    ownMonster,
    speciesTendency,
    hpMods,
    reflectorMods,
    /* counterMods */ undefined,
    /* opponentAbilityMods */ undefined,
    /* matchupDistanceMods */ undefined,
    /* successPatternMods */ undefined,
    /* metaReadingMods */ undefined,
    /* selfAbilityMods */ undefined,
    selfReflectorMods,
    configMods,
  );

  // スコア降順でソートして上位 M 個を取得
  const sorted = Array.from(pairScores.values()).sort((a, b) => b.score - a.score);
  const top = sorted.slice(0, topM);
  return top.map((entry) => ({
    first: { type: entry.first },
    second: { type: entry.second },
  }));
}

/**
 * 種族傾向にアーキタイプ推定バイアスを乗算合成する
 *
 * 履歴がない（=試合開始直後）場合は速やかに speciesTendency をそのまま返す。
 * 履歴がある場合はベイズ推定でアーキタイプ事後確率を求め、行動傾向を補正する。
 *
 * `aiConfig.opponentModeling` の値で補正の強さを調整:
 * - 0: アーキタイプ推定無効（speciesTendency そのまま）
 * - 100: アーキタイプ推定の影響最大
 *
 * @internal
 */
function applyArchetypeBias(
  baseTendency: SpeciesTendency,
  turnHistory: TurnResult[],
  opponentPlayerId: 'player1' | 'player2',
  opponentAIConfig: AIConfig,
): SpeciesTendency {
  if (turnHistory.length === 0) return baseTendency;
  const strength = opponentAIConfig.opponentModeling / 100;
  if (strength <= 0) return baseTendency;

  const posterior: ArchetypePosterior = inferOpponentArchetype(turnHistory, opponentPlayerId);
  const bias = getArchetypeWeightedCommandBias(posterior);

  // strength=0 で baseTendency と同じ、strength=1 で archetypeBias 強化版
  // 線形補間: result = baseTendency * (1 + (bias - 1) * strength)
  const result = { ...baseTendency };
  for (const cmd of Object.values(CommandType)) {
    const baseBias = bias[cmd] ?? 1.0;
    const blendedFactor = 1 + (baseBias - 1) * strength;
    result[cmd] = baseTendency[cmd] * blendedFactor;
  }
  return result;
}
